import dayjs, {Dayjs} from 'dayjs';
import {apiPost} from '../../../../axiosConfig';
import {IStation} from '../../../../dto/Station.dto';
import {ITimedDuplicateTimes, iTimedMsgDateRange} from './TimedMessageRequest';
import {iTimedMsgTimesItem} from './times/TimedMsgRequestTimesCard';

type DuplicateTimeResponse = {
  conflict_date: string;
  conflict_day: string;
  conflict_time: string;
  start_date: string;
  end_date: string;
};

type DayTime = {
  days: string[];
  times: string[];
};

type Payload = {
  type: string;
  day_type: iDayType;
  date_ranges: {
    start_date: string;
    end_date: string;
    day_times: DayTime[];
  }[];
  station_ids: number[];
  timed_id?: number;
};

const formatTime = (time: Dayjs | null): string | null =>
  time ? time.format('HH:mm:00') : null;

const formatDate = (date: Dayjs | null): string | null =>
  date ? date.format('YYYY-MM-DD') : null;

const buildDayTimes = (source: iTimedMsgTimesItem[]): DayTime[] => {
  return source
    .filter(dt => dt.selectedDays?.length && dt.timeRanges?.length)
    .map(dt => ({
      days: dt.selectedDays.map(d => d.toLowerCase()),
      times: dt.timeRanges
        .map(tr => formatTime(tr.start_time))
        .filter((time): time is string => time !== null),
    }));
};

const buildDateRange = (
  range: iTimedMsgDateRange,
  dayTimes?: iTimedMsgTimesItem[],
): {
  start_date: string;
  end_date: string;
  day_times: DayTime[];
} => {
  const startDate = formatDate(range.start_date);
  const endDate = formatDate(range.end_date);

  if (!startDate || !endDate) {
    throw new Error('Invalid date range - start or end date is null');
  }

  return {
    start_date: startDate,
    end_date: endDate,
    day_times: dayTimes
      ? buildDayTimes(dayTimes)
      : range.day_times
      ? buildDayTimes(range.day_times)
      : [],
  };
};

const buildPayload = (
  dayType: iDayType,
  dateRanges: iTimedMsgDateRange[],
  daysTimes: iTimedMsgTimesItem[],
  selectedStations: IStation[],
  updateId?: number,
  restoreId?: number,
): Payload => {
  if (!['multiple', 'no-end', 'one-day'].includes(dayType)) {
    throw new Error(`Unhandled dayType: ${dayType}`);
  }

  const payload: Payload = {
    type: 'Active',
    day_type: dayType,
    date_ranges: dateRanges.map(range =>
      dayType === 'multiple'
        ? buildDateRange(range)
        : buildDateRange(range, daysTimes),
    ),
    station_ids: selectedStations.map(s => s.id),
  };

  if (restoreId || updateId) {
    payload.timed_id = restoreId ?? updateId;
  }

  return payload;
};

type iDayType = 'multiple' | 'no-end' | 'one-day';

const timedMsgTimeDuplicationValidation = async (
  companyId: number,
  dateRanges: iTimedMsgDateRange[],
  daysTimes: iTimedMsgTimesItem[],
  setDuplicateTimes: React.Dispatch<
    React.SetStateAction<ITimedDuplicateTimes[]>
  >,
  dayType: iDayType,
  selectedStations: IStation[],
  setIsTimeUniqueLoading: (val: boolean) => void,
  restoreId?: number,
  updateId?: number,
  abortController?: AbortController,
) => {
  setIsTimeUniqueLoading(true);

  try {
    const submitUrl = `/v1/company/${companyId}/message/timed-message/check-time-exist`;

    const payload = buildPayload(
      dayType,
      dateRanges,
      daysTimes,
      selectedStations,
      updateId,
      restoreId,
    );

    const response = await apiPost(submitUrl, payload, {
      signal: abortController?.signal,
    });

    if (abortController?.signal.aborted) return;

    setDuplicateTimes([]);

    if (response.data.length) {
      const map = new Map<string, Set<string>>();

      for (const conflict of response.data as DuplicateTimeResponse[]) {
        const date = conflict.conflict_date;
        const time = conflict.conflict_time;

        if (!map.has(date)) {
          map.set(date, new Set());
        }
        map.get(date)?.add(time);
      }

      const val = Array.from(map.entries()).map(([date, times]) => ({
        date: dayjs(date),
        times: Array.from(times),
      }));

      setDuplicateTimes(val);
    }
    setIsTimeUniqueLoading(false);
  } catch (error) {
    setDuplicateTimes([]);
    setIsTimeUniqueLoading(false);
  }
};

export default timedMsgTimeDuplicationValidation;
